import { useEffect, useMemo, useState } from "react";
import { Bell, Calendar, Clock3, MoreVertical, Star } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const doctorCache = new Map();

export default function AppointmentTimeline() {
  const { profile } = useAuth();
  const user = profile;
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [noticeMessage, setNoticeMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const normalizeStatus = (status) => String(status || "").trim().toLowerCase();
  const upcomingStatuses = new Set(["scheduled", "ongoing", "confirmed"]);
  const completedStatuses = new Set(["completed", "complete", "done", "finished"]);
  const cancelledStatuses = new Set(["cancelled", "canceled", "cancel"]);
  const resolvedUserEmail =
    user?.email ||
    (() => {
      try {
        const stored = localStorage.getItem("user");
        return stored ? JSON.parse(stored)?.email : null;
      } catch {
        return null;
      }
    })();
  const resolvedUserId =
    user?._id ||
    localStorage.getItem("userId") ||
    (() => {
      try {
        const stored = localStorage.getItem("user");
        return stored ? JSON.parse(stored)?._id : null;
      } catch {
        return null;
      }
    })();

  useEffect(() => {
    const notice = location?.state?.notice;
    if (!notice) return;

    setNoticeMessage(notice);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!resolvedUserId) {
      setAppointments([]);
      setLoading(false);
      return;
    }

    let isCancelled = false;

    async function fetchAppointments() {
      setLoading(true);
      try {
        const res = await api.get(`/api/consultations/patient/${resolvedUserId}`);
        const payload = res?.data;
        let appts = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.consultations)
          ? payload.consultations
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        // Some historical consultations may be linked via patient_ or patientEmail.
        if (appts.length <= 1) {
          try {
            const allRes = await api.get(`/api/consultations`);
            const allPayload = allRes?.data;
            const allConsultations = Array.isArray(allPayload)
              ? allPayload
              : Array.isArray(allPayload?.consultations)
              ? allPayload.consultations
              : Array.isArray(allPayload?.data)
              ? allPayload.data
              : [];

            const merged = [...appts, ...allConsultations].filter((appt) => {
              const patientId = appt?.patient?._id || appt?.patient;
              const nestedPatientId = appt?.patient_?._id;
              const patientEmail = appt?.patientEmail || appt?.patient_?.email;

              return (
                String(patientId || "") === String(resolvedUserId) ||
                String(nestedPatientId || "") === String(resolvedUserId) ||
                (resolvedUserEmail && String(patientEmail || "").toLowerCase() === String(resolvedUserEmail).toLowerCase())
              );
            });

            const uniqueById = new Map();
            merged.forEach((appt) => {
              const key = String(appt?._id || appt?.id || "");
              if (!key) return;
              if (!uniqueById.has(key)) uniqueById.set(key, appt);
            });

            appts = Array.from(uniqueById.values());
          } catch (fallbackErr) {
            console.error("Fallback consultations fetch failed:", fallbackErr);
          }
        }

        appts.sort((a, b) => new Date(b?.appointmentTime || 0) - new Date(a?.appointmentTime || 0));

        const fallbackDoctor = { name: "Unknown", specialty: "", rating: 0 };

        // Fetch each unique doctor once, then map back to all appointments.
        const uniqueDoctorIds = [...new Set(
          appts
            .map((appt) => appt?.doctor)
            .filter((doctorRef) => doctorRef && typeof doctorRef !== "object")
            .map((id) => String(id))
        )];

        const doctorEntries = await Promise.all(
          uniqueDoctorIds.map(async (doctorId) => {
            if (doctorCache.has(doctorId)) {
              return [doctorId, doctorCache.get(doctorId)];
            }

            try {
              const docRes = await api.get(`/api/doctor/${doctorId}`);
              doctorCache.set(doctorId, docRes.data);
              return [doctorId, docRes.data];
            } catch (err) {
              console.error("Failed to fetch doctor info:", err);
              return [doctorId, fallbackDoctor];
            }
          })
        );

        const doctorById = new Map(doctorEntries);

        const apptsWithDoctor = appts.map((appt) => ({
          ...appt,
          doctor:
            typeof appt?.doctor === "object"
              ? appt.doctor
              : doctorById.get(String(appt?.doctor)) || fallbackDoctor,
        }));

        if (!isCancelled) {
          setAppointments(apptsWithDoctor);
        }
      } catch (err) {
        console.error("Failed to fetch appointments:", err);
        if (!isCancelled) {
          setAppointments([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    }

    fetchAppointments();

    return () => {
      isCancelled = true;
    };
  }, [resolvedUserId]);

  const visibleAppointments = useMemo(() => {
    const now = new Date();
    const ONGOING_WINDOW_MS = 2 * 60 * 60 * 1000;

    return appointments.filter((a) => {
      const status = normalizeStatus(a.status);
      const when = new Date(a.appointmentTime);
      const startsAtMs = when.getTime();
      const isValidStartTime = Number.isFinite(startsAtMs);
      const isActiveStatus = upcomingStatuses.has(status);
      const isWithinUpcomingWindow =
        isActiveStatus &&
        isValidStartTime &&
        now.getTime() <= startsAtMs + ONGOING_WINDOW_MS;

      if (activeTab === "upcoming") {
        if (isWithinUpcomingWindow) {
          return true;
        }

        return false;
      }
      if (activeTab === "complete") {
        return completedStatuses.has(status) || (when < now && !cancelledStatuses.has(status));
      }
      return cancelledStatuses.has(status);
    });
  }, [appointments, activeTab]);

  const formatDate = (dateValue) => {
    const d = new Date(dateValue);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
    });
  };

  const formatDuration = (appt) => {
    if (appt.durationMinutes && Number(appt.durationMinutes) > 0) {
      const mins = Number(appt.durationMinutes);
      return mins >= 60 ? `${Math.round(mins / 60)} Hours` : `${mins} Minutes`;
    }
    return "01 Hours";
  };

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", minHeight: "100vh", background: "var(--BG)", paddingBottom: 92 }}>
      <style>{`
        .booking-wrap { padding: 14px 14px 0; }
        .booking-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
        .booking-head-left { display:flex; align-items:center; gap:10px; }
        .booking-title { margin:0; font-size:30px; font-weight:800; color: var(--on-bg); letter-spacing: -0.02em; }
        .back-btn { border:none; background:transparent; color: var(--on-bg); width:36px; height:36px; display:flex; align-items:center; justify-content:center; border-radius:10px; }
        .bell-btn { border:none; background:transparent; color: var(--on-bg); width:36px; height:36px; display:flex; align-items:center; justify-content:center; border-radius:10px; }
        .tab-row { display:flex; gap:8px; margin-bottom:14px; background:#f1f1f1; border-radius:12px; padding:4px; }
        .tab-btn { flex:1; border:none; background:transparent; color:#7b7b7b; font-size:14px; font-weight:600; border-radius:10px; padding:10px 6px; }
        .tab-btn.active { background:#fff; color:#161616; box-shadow: 0 1px 2px rgba(0,0,0,0.08); }
        .appt-card { background:#fff; border:1px solid #ececec; border-radius:14px; padding:12px; margin-bottom:10px; }
        .appt-top { display:flex; gap:10px; align-items:flex-start; }
        .appt-avatar { width:46px; height:46px; border-radius:50%; object-fit:cover; border:1px solid #ececec; }
        .appt-main { flex:1; min-width:0; }
        .appt-name { margin:0; font-size:20px; font-weight:700; color:#171717; line-height:1.15; }
        .appt-specialty { margin:2px 0 4px; font-size:13px; color:#808080; }
        .appt-rating { display:flex; align-items:center; gap:4px; color:#252525; font-size:13px; font-weight:600; }
        .appt-menu { border:none; background:transparent; color:#7a7a7a; width:28px; height:28px; display:flex; align-items:center; justify-content:center; border-radius:8px; }
        .appt-bottom { display:flex; align-items:center; justify-content:space-between; margin-top:10px; gap:10px; }
        .appt-meta { display:flex; align-items:center; gap:14px; color:#6e6e6e; font-size:13px; font-weight:500; }
        .meta-item { display:flex; align-items:center; gap:6px; }
        .details-btn { border:none; background:transparent; color:#3f1ecf; font-size:13px; font-weight:700; padding:0; display:flex; align-items:center; gap:6px; }
      `}</style>

      <div className="booking-wrap">
        <div className="booking-head">
          <div className="booking-head-left">
            <button className="back-btn" aria-label="Go back" onClick={() => navigate(-1)}>
              <i className="fi fi-br-angle-left" style={{ fontSize: 18 }}></i>
            </button>
            <h2 className="booking-title">My Booking</h2>
          </div>
          <button className="bell-btn" aria-label="Notifications">
            <Bell size={20} />
          </button>
        </div>

        <div className="tab-row">
          <button className={`tab-btn ${activeTab === "upcoming" ? "active" : ""}`} onClick={() => setActiveTab("upcoming")}>Upcoming</button>
          <button className={`tab-btn ${activeTab === "complete" ? "active" : ""}`} onClick={() => setActiveTab("complete")}>Complete</button>
          <button className={`tab-btn ${activeTab === "cancel" ? "active" : ""}`} onClick={() => setActiveTab("cancel")}>Cancel</button>
        </div>
      </div>

      <div className="booking-wrap">
        {noticeMessage ? (
          <div className="alert alert-success" role="alert" style={{ marginBottom: 10 }}>
            {noticeMessage}
          </div>
        ) : null}

        {loading ? (
          <p>Loading appointments...</p>
        ) : visibleAppointments.length === 0 ? (
          <p>No appointments in this section.</p>
        ) : (
          visibleAppointments.map((appt) => (
            <div key={appt._id || appt.id} className="appt-card">
              <div className="appt-top">
                <img
                  className="appt-avatar"
                  src={appt?.doctor?.avatar || "https://randomuser.me/api/portraits/men/1.jpg"}
                  alt={appt?.doctor?.name || "Doctor"}
                />

                <div className="appt-main">
                  <p className="appt-name">{appt?.doctor?.name || "Unknown Doctor"}</p>
                  <p className="appt-specialty">{appt?.doctor?.specialty || "Specialist"}</p>
                  <div className="appt-rating">
                    <Star size={12} fill="#ef4444" color="#ef4444" />
                    {Number(appt?.doctor?.averageRating || appt?.doctor?.rating || 5).toFixed(1)}
                  </div>
                </div>

                <button className="appt-menu" aria-label="More options">
                  <MoreVertical size={16} />
                </button>
              </div>

              <div className="appt-bottom">
                <div className="appt-meta">
                  <span className="meta-item"><Calendar size={13} />{formatDate(appt.appointmentTime)}</span>
                  <span className="meta-item"><Clock3 size={13} />{formatDuration(appt)}</span>
                </div>

                <button
                  className="details-btn"
                  onClick={() => navigate("/appointment-card", { state: { consultation: appt } })}
                >
                  <span style={{ fontSize: 12 }}>▣</span> View details
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
